/**
 * Video consultations (LiveKit) — medopd-backend `videoconsult` package, doc 40.
 *
 * Three callers, three credentials, one shape:
 * - staff (cookie session): create, state, doctor join, end, cancel, notify;
 * - signed-in patients (portal session), keyed by APPOINTMENT id;
 * - people who booked without an account: a visit-scoped pass from "manage my booking", sent in bodies only.
 *
 * A join returns room credentials only when the caller may connect now. They are short-lived; never store them
 * and never put them in a URL. A new connection always goes back through `join`.
 */
import { fetchApi, fetchPatientApi, type ApiResponse } from '../client';

export type VideoState = 'SCHEDULED' | 'WAITING' | 'ACTIVE' | 'ENDED' | 'CANCELLED' | 'EXPIRED';

export type VideoBlockedCode =
  | 'TOO_EARLY' | 'NOT_CONFIRMED' | 'CONSENT_REQUIRED' | 'ENDED' | 'CANCELLED' | 'EXPIRED' | 'NOT_ASSIGNED';

export interface VideoVisit {
  id: string;
  appointmentId: string;
  state: VideoState;
  scheduledStart: string | null;
  joinOpensAt: string | null;
  joinClosesAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
  /** Why it ended, e.g. DOCTOR_NO_SHOW (the doctor never joined: cancelled + refunded) or PATIENT_NO_SHOW. */
  endReason: string | null;
  consentRecorded: boolean;
  consentVersion: string;
  /** The patient is in the waiting room right now (fresh heartbeat). */
  patientWaiting: boolean;
  doctorConnected: boolean;
  patientConnected: boolean;
  doctorName: string | null;
  clinicName: string | null;
  appointmentDate: string | null;
  appointmentTime: string | null;
  canJoin: boolean;
  blockedCode: VideoBlockedCode | null;
  blockedReason: string | null;
  /** Minutes the doctor said they'd be late ("running late"); 0 when none. */
  delayMinutes?: number;
  /** The booked start plus that delay (local date-time), when timed. */
  expectedStart?: string | null;
  /** Past the (delayed) start and the doctor hasn't joined yet. */
  doctorLate?: boolean;
}

export interface VideoCredentials {
  token: string;
  wsUrl: string;
  expiresAt: string;
}

/** `credentials` is null while the patient waits for the doctor. */
export interface VideoJoinResult {
  view: VideoVisit;
  credentials: VideoCredentials | null;
}

export interface VideoServiceStatus {
  configured: boolean;
  clinicEnabled: boolean;
}

/** The three operations every patient-side screen needs, whichever credential backs them. */
export interface PatientVideoApi {
  state: () => Promise<ApiResponse<VideoVisit>>;
  consent: () => Promise<ApiResponse<VideoVisit>>;
  join: () => Promise<ApiResponse<VideoJoinResult>>;
}

const post = (body?: unknown): RequestInit => ({ method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });

export const videoConsultationModule = {
  // ── Staff ──────────────────────────────────────────────────────────────────────────────────
  serviceStatus: () => fetchApi<VideoServiceStatus>('/api/video-consultations/service-status'),
  create: (appointmentId: string) => fetchApi<VideoVisit>('/api/video-consultations', post({ appointmentId })),
  /** `data` is null when the appointment has no video visit. */
  byAppointment: (appointmentId: string) =>
    fetchApi<VideoVisit | null>(`/api/video-consultations/by-appointment/${encodeURIComponent(appointmentId)}`),
  /** Appointment id → state, for lists. */
  states: (appointmentIds: string[]) => fetchApi<Record<string, VideoState>>('/api/video-consultations/states', post(appointmentIds)),
  join: (id: string) => fetchApi<VideoJoinResult>(`/api/video-consultations/${encodeURIComponent(id)}/join`, post()),
  end: (id: string) => fetchApi<VideoVisit>(`/api/video-consultations/${encodeURIComponent(id)}/end`, post()),
  cancel: (id: string) => fetchApi<VideoVisit>(`/api/video-consultations/${encodeURIComponent(id)}/cancel`, post()),
  /** "Running late by N minutes" (5–120, total from the booked time): the patient is told the new expected time. */
  delay: (id: string, minutes: number) =>
    fetchApi<VideoVisit>(`/api/video-consultations/${encodeURIComponent(id)}/delay`, post({ minutes })),
  notifyPatient: (id: string) =>
    fetchApi<{ channelsSent: number }>(`/api/video-consultations/${encodeURIComponent(id)}/notify-patient`, post()),

  // ── Signed-in patient (portal session; dependant-aware through the usual headers) ──────────────
  /** `clinicId`: the appointment's own linked clinic when it isn't the active one (medopd-docs 137); the server accepts
   *  it only for a clinic linked to this login, and still checks the appointment is this person's. */
  patient: (appointmentId: string, clinicId?: string | null): PatientVideoApi => {
    const base = `/api/patient/video-consultations/by-appointment/${encodeURIComponent(appointmentId)}`;
    const scoped = (init: RequestInit = {}): RequestInit =>
      clinicId ? { ...init, headers: { ...(init.headers as Record<string, string> | undefined), 'X-Active-Patient-Clinic-Id': clinicId } } : init;
    return {
      state: () => fetchPatientApi<VideoVisit>(base, scoped()),
      consent: () => fetchPatientApi<VideoVisit>(`${base}/consent`, scoped(post())),
      join: () => fetchPatientApi<VideoJoinResult>(`${base}/join`, scoped(post())),
    };
  },

  // ── Booked without an account ────────────────────────────────────────────────────────────────
  /** Spends one "manage my booking" grant use; returns a pass bound to this one visit. */
  guestPass: (grant: string, reference: string) =>
    fetchPatientApi<{ pass: string; expiresInSeconds: number; visit: VideoVisit }>(
      '/api/public/booking/manage/video/pass', post({ grant, reference })),
  guest: (pass: string): PatientVideoApi => ({
    state: () => fetchPatientApi<VideoVisit>('/api/public/booking/manage/video/state', post({ pass })),
    consent: () => fetchPatientApi<VideoVisit>('/api/public/booking/manage/video/consent', post({ pass })),
    join: () => fetchPatientApi<VideoJoinResult>('/api/public/booking/manage/video/join', post({ pass })),
  }),
};
